import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeStyles } from "@fluentui/react-components";
import { Add16Filled } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { checkUserPermission } from "../../../../utils";
import { UserPermissionsList } from "../../../../config";
import {
  clearAllAssetsData,
  getAssetListItems,
} from "../../../../redux/Case/assetSlice";
import ASCButton from "../../../../components/Button";
import NoDataFound from "../../../../components/NoDataFound/noDataFound";
import CaseList from "../CaseList";
import { normalizeCaseType, isCurrentStatus, getItemStatus } from "../../../../shared/caseActiveStatus";
import { ASSET_CASE_TYPE_MAP } from "./assetSubTypeConfig";

type Phase =
  | "checking"            // single fetch: all records, client-side status filter
  | "has-non-current-only"
  | "truly-empty"
  | "navigating";

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    justifyContent: "flex-end",
    margin: "10px 10px 0",
  },
});

const AssetHomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, case_type, action } = useParams<{
    clientId: string;
    case_type?: string;
    action?: string;
  }>();

  const { authUser, assets } = useSelector((state: RootState) => ({
    authUser: state.authUser,
    assets: state.assets,
  }));

  const styles = useStyles();

  useEffect(() => {
    if (clientId && !case_type) {
      navigate(`/clients/${clientId}/assets/investment`, { replace: true });
    }
  }, [clientId, case_type, navigate]);

  const dbCaseType = case_type ? ASSET_CASE_TYPE_MAP[case_type] : undefined;

  const [phase, setPhase] = useState<Phase>("checking");
  const requestIdRef = useRef(0);

  useEffect(() => {
    setPhase("checking");
  }, [case_type]);

  // Single fetch — all records, no status filter. Status is evaluated
  // client-side using normalizeStatus so casing/separator variants in
  // the DB ("ACTIVE", "in-force", "In Force") all match correctly.
  useEffect(() => {
    if (!clientId || !case_type || phase !== "checking") return;

    const requestId = ++requestIdRef.current;

    dispatch(clearAllAssetsData());
    dispatch(getAssetListItems({ customerId: clientId, caseType: dbCaseType }))
      ?.then?.((result: any) => {
        if (requestIdRef.current !== requestId) return;
        const items = Array.isArray(result?.payload) ? result.payload : [];
        const byType = dbCaseType
          ? items.filter((x: any) => normalizeCaseType(x.case_type) === normalizeCaseType(dbCaseType))
          : items;

        if (byType.length === 0) {
          setPhase("truly-empty");
        } else if (byType.some((x: any) => isCurrentStatus("asset", getItemStatus(x)))) {
          setPhase("navigating");
        } else {
          setPhase("has-non-current-only");
        }
      });
  }, [clientId, case_type, dbCaseType, phase, dispatch]);

  const matchingAllItems = useMemo(() => {
    return dbCaseType
      ? assets.listItems.filter((item: any) => normalizeCaseType(item.case_type) === normalizeCaseType(dbCaseType))
      : assets.listItems;
  }, [assets.listItems, dbCaseType]);

  const matchingCurrentItems = useMemo(() => {
    return matchingAllItems.filter((item: any) => isCurrentStatus("asset", getItemStatus(item)));
  }, [matchingAllItems]);

  useEffect(() => {
    if (phase !== "navigating" || !case_type) return;
    const target = matchingCurrentItems.length > 0 ? matchingCurrentItems[0] : matchingAllItems[0];
    if (!target) return;
    navigate(
      `/clients/${clientId}/assets/${case_type}/${target?.asset_id}/asset/${action || "view"}`
    );
  }, [clientId, case_type, action, navigate, matchingCurrentItems, matchingAllItems, phase]);

  const handleAddNew = useCallback(() => {
    navigate(`/clients/${clientId}/assets/${case_type}/add`);
  }, [clientId, case_type, navigate]);

  // Don't render during loading or active fetching phases
  if (!case_type || assets.loading) return null;
  if (phase === "checking" || phase === "navigating") return null;

  const addNewButton = checkUserPermission(
    authUser,
    UserPermissionsList.ALLOW_UPDATE_CLIENT
  ) && (
    <ASCButton
      shape="rounded"
      appearance="primary"
      className="asc-button-primary"
      icon={<Add16Filled />}
      size="medium"
      onClick={handleAddNew}
      label="Add New"
    />
  );

  // Scenario 1: non-current records exist but no current ones.
  // Show the same table with toggle button and a message.
  // Clicking "Current Cases" toggle navigates to the first non-current record
  // (matchingItems is already populated from the checking-non-current fetch).
  if (phase === "has-non-current-only") {
    return (
      <div>
        <div className={styles.wrapper}>{addNewButton}</div>
        <div style={{ marginTop: "10px" }}>
          <CaseList
            currentRecord={1}
            setCurrentRecord={() => {}}
            module="assets"
            data={[]}
            activeItem={null}
            section="assets"
            tab="asset"
            showNonCurrent={false}
            onToggleNonCurrent={() => setPhase("navigating")}
            emptyMessage='No active records for current cases. Click "Non-current Cases" to view non-current records.'
          />
        </div>
      </div>
    );
  }

  // Scenario 2: no records at all — no table, just NoDataFound
  return (
    <div>
      <div className={styles.wrapper}>{addNewButton}</div>
      <NoDataFound />
    </div>
  );
};

export default AssetHomePage;
